import React, { useState } from "react";
import {
  Box,
  Chip,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Tabs,
  Tab
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useCategorizedTransactions } from "./hooks/useCategorizedTransactions";
import { UserCategoriesSection } from "./UserCategoriesSection";
import { BudgetStatusSection } from "./BudgetStatusSection";

const buildColumns = (onViewRule) => [
  {
    field: "transactionId",
    headerName: "Transaction ID",
    width: 260
  },
  {
    field: "transactionDate",
    headerName: "Date",
    width: 110
  },
  {
    field: "memo",
    headerName: "Memo",
    width: 200,
    flex: 1
  },
  {
    field: "vendorName",
    headerName: "Vendor",
    width: 160
  },
  {
    field: "userCategory",
    headerName: "User Category",
    width: 160
  },
  {
    field: "joroSubcategory",
    headerName: "Joro Subcategory",
    width: 180
  },
  {
    field: "userCategorySource",
    headerName: "Source",
    width: 90,
    renderCell: (params) => params.value
      ? (
        <Chip
          label={params.value}
          color={params.value === "manual" ? "primary" : "default"}
          size="small"
        />
      )
      : null
  },
  {
    field: "ruleDescription",
    headerName: "Matched Rule",
    width: 160,
    renderCell: (params) => params.value
      ? (
        <Button
          size="small"
          onClick={() => onViewRule(params.value)}
        >
          View Rule
        </Button>
      )
      : null
  }
];

export const BudgetsSection = ({ userId }) => {
  const [activeSubTab, setActiveSubTab] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25
  });
  const [transactionIdInput, setTransactionIdInput] = useState("");
  const [transactionIdFilter, setTransactionIdFilter] = useState("");
  const [selectedRuleDescription, setSelectedRuleDescription] = useState(null);

  const { data, isLoading } = useCategorizedTransactions({
    userId,
    page: paginationModel.page + 1,
    pageSize: paginationModel.pageSize,
    transactionId: transactionIdFilter
  });

  const rows = data?.transactions?.map((row) => ({
    ...row,
    id: row.transactionId
  })) ?? [];

  const columns = buildColumns(setSelectedRuleDescription);

  const handleSearch = () => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setTransactionIdFilter(transactionIdInput.trim());
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleClear = () => {
    setTransactionIdInput("");
    setTransactionIdFilter("");
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  return (
    <Box>
      <Tabs
        value={activeSubTab}
        onChange={(_, newValue) => setActiveSubTab(newValue)}
        sx={{ marginBottom: 2 }}
      >
        <Tab label="Transactions" />
        <Tab label="Categories & Rules" />
        <Tab label="Budgets" />
      </Tabs>

      {activeSubTab === 0 && (
        <Box>
          <Box sx={{ display: "flex", gap: 2, marginBottom: 2 }}>
            <TextField
              label="Transaction ID"
              variant="outlined"
              size="small"
              value={transactionIdInput}
              onChange={(e) => setTransactionIdInput(e.target.value)}
              onKeyDown={handleKeyPress}
              sx={{ width: 340 }}
            />
            <Button variant="contained" onClick={handleSearch}>
              Search
            </Button>
            {transactionIdFilter && (
              <Button variant="text" onClick={handleClear}>
                Clear
              </Button>
            )}
          </Box>

          <Box sx={{ height: 600 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              rowCount={data?.total ?? 0}
              paginationModel={paginationModel}
              paginationMode="server"
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[25, 50, 100]}
              loading={isLoading}
              disableRowSelectionOnClick
              slotProps={{
                loadingOverlay: {
                  variant: "linear-progress",
                  noRowsVariant: "skeleton"
                }
              }}
            />
          </Box>
        </Box>
      )}

      {activeSubTab === 1 && <UserCategoriesSection userId={userId} />}

      {activeSubTab === 2 && <BudgetStatusSection userId={userId} />}

      <Dialog
        open={!!selectedRuleDescription}
        onClose={() => setSelectedRuleDescription(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Matched Rule</DialogTitle>
        <DialogContent>
          <Typography sx={{ whiteSpace: "pre-wrap" }}>
            {selectedRuleDescription}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedRuleDescription(null)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
